import React, { useState, useEffect } from 'react';
import { WifiOff, Wifi, AlertCircle } from 'lucide-react';

/**
 * OfflineIndicator - Shows network status and sync information
 */
export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastSync(new Date());
      console.log('[OfflineIndicator] Back online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[OfflineIndicator] Offline detected');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) {
    return null; // Don't show indicator when online
  }

  return (
    <div 
      className="offline-indicator"
      role="alert"
      aria-live="assertive"
      data-testid="offline-indicator"
    >
      <WifiOff size={20} />
      <span>You're offline</span>
      <span className="offline-hint">Changes will be synced when connection is restored</span>
    </div>
  );
};

/**
 * OnlineStatus Hook - Provides current network status
 */
export const useOnlineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};

/**
 * SyncStatus Component - Shows synchronization status
 */
export const SyncStatus = ({ isSyncing, lastSync, pendingChanges = 0 }) => {
  if (pendingChanges === 0 && !isSyncing && !lastSync) {
    return null;
  }

  return (
    <div className="sync-status" data-testid="sync-status">
      {isSyncing ? (
        <>
          <div className="sync-spinner" />
          <span>Syncing changes...</span>
        </>
      ) : (
        <>
          <Wifi size={16} />
          <span>Last synced: {lastSync ? new Date(lastSync).toLocaleTimeString() : 'Never'}</span>
          {pendingChanges > 0 && (
            <span className="pending-changes-badge">{pendingChanges} pending</span>
          )}
        </>
      )}
    </div>
  );
};

export default OfflineIndicator;
